import { useEffect, useRef, useState } from "react";
import { CharacterTextSplitter } from "langchain/text_splitter";

import OpenAI from "openai";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Input from "@mui/material/Input";
import Delete from "@mui/icons-material/Delete";
import LinearProgress from "@mui/material/LinearProgress";

import { HttpCustomApi } from "../../interface/custom-api";
import { HttpMainApi } from "../../interface/main-api";
import { userState, systemIndex, tennantInfo } from "../../interface/MainInterface";
import LoadingCircle from "../../utils/LoadingCircle";
import Toast from "../../utils/Toast";

import PopAnswers from "./PopAnswers";

interface propsType {
  userState: userState;
  tennantInfo: tennantInfo;
}

const mainApi = new HttpMainApi();
const vectoCoreApi = new HttpCustomApi(process.env.REACT_APP_VECTOR_CORE);

const openai = new OpenAI({
  apiKey: "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",
  dangerouslyAllowBrowser: true,
});

const fileGroup: any = [
  "application/pdf",
  "text/csv",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/mspowerpoint",
  "application/x-mspowerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/msword",
  "application/csv",
  "application/excel",
];

const MngDocs = (props: propsType) => {
  const toastRef: any = useRef();
  const popAnswerRef: any = useRef();
  const attachInputRef: any = useRef();

  const [isLoading, setIsLoading] = useState(false);
  const [indexes, setIndexes] = useState<any>([]);
  const [selectedIndexName, setSelectedIndexName] = useState("-1");
  const [docs, setDocs] = useState<any>([]);
  const [selectedModel, setSelectedModel] = useState("naver");

  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState<any>([]);

  const [progress, setProgress] = useState(0);

  useEffect(() => {
    getIndexes();
    return () => {
      setIsLoading(false);
      setIndexes([]);
      setSelectedIndexName("-1");
      setSelectedModel("naver");
      setDocs([]);
      setQuestion("");
      setAnswers([]);
      setProgress(0);
    };
  }, []);

  const getIndexes = async () => {
    setIsLoading(true);
    const res = await vectoCoreApi.auth_request("api", { command: "list_index" }, props.tennantInfo.tennant_key);
    console.log("res : ", res);
    if (res.code === "200") {
      let temp_lit = [];
      for (const index of res.response.index_list) {
        if (!systemIndex.includes(index) && index.substr(0, 6) !== "naverx") temp_lit.push(index);
      }
      console.log("temp_lit : ", temp_lit);
      setIndexes(temp_lit.sort());
      setSelectedIndexName(temp_lit.sort()[0]);
      getDocs(temp_lit.sort()[0]);
    } else {
      console.error("getIndexes : ", res.response.error_msg);
    }

    setIsLoading(false);
  };

  const getEmbeddingText = async (_text: string) => {
    if (selectedModel === "openai") {
      const result = await openai.embeddings.create({
        input: _text,
        model: "text-embedding-ada-002",
      });

      return result.data[0].embedding;
    } else if (selectedModel === "naver") {
      return await mainApi.naver_embedding({ text: _text });
    }
  };

  const getDocs = async (_indexName: string) => {
    setProgress(0);
    const param: any = {
      command: "list_doc",
      index_name: _indexName,
    };

    setDocs([]);
    setIsLoading(true);
    const res = await vectoCoreApi.auth_request("api", param, props.tennantInfo.tennant_key);
    // console.log("res : ", res);
    if (res.code === "200") {
      setDocs(
        res.response.document_list.sort(function (a: any, b: any) {
          if (a.id < b.id) return -1;
          else if (a.id > b.id) return 1;
          else return 0;
        })
      );
    }
    setIsLoading(false);
  };

  const deleteDoc = async (_docId: string) => {
    if (selectedIndexName === "-1" || selectedIndexName === undefined) {
      toastRef.current?.toast("Please select index", "error", 3000, { vertical: "top", horizontal: "center" });
      return;
    }
    if (!window.confirm("Delete document?")) return;

    const param: any = {
      command: "delete_doc",
      index_name: selectedIndexName,
      doc_id: _docId,
    };
    setIsLoading(true);
    const res = await vectoCoreApi.auth_request("api", param, props.tennantInfo.tennant_key);
    if (res.code === "200") {
      console.log("deleteDoc result : ", res.response.result);
      toastRef.current?.toast("Success delete document", "success", 3000, { vertical: "top", horizontal: "center" });
      getDocs(selectedIndexName);
    } else {
      toastRef.current?.toast("Error : " + res.response.error_msg, "error", 3000, { vertical: "top", horizontal: "center" });
    }
    setIsLoading(false);
  };

  const handelSelectedIndexChange = (event: SelectChangeEvent) => {
    setSelectedIndexName(event.target.value as string);
  };

  const handelSelectedModelChange = (event: SelectChangeEvent) => {
    setSelectedModel(event.target.value as string);
  };

  useEffect(() => {
    if (selectedIndexName !== undefined) {
      getDocs(selectedIndexName);
    }
  }, [selectedIndexName]);

  function DocsRow(props: { docInfo: ReturnType<any> }) {
    const { docInfo } = props;
    const [open, setOpen] = useState(false);

    return (
      <>
        <TableRow key={docInfo.id} sx={open ? {} : { "& > *": { borderBottom: "unset" } }}>
          <TableCell width={20}>
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell component="th" scope="row">
            {docInfo.id}
          </TableCell>
          <TableCell align="right">
            <IconButton
              onClick={() => {
                deleteDoc(docInfo.id);
              }}
            >
              <Delete className="data-table-img-button color-red" />
            </IconButton>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2, whiteSpace: "pre-line" }}>{docInfo.text}</Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }

  const fncQuestion = async () => {
    if (selectedIndexName === "-1" || selectedIndexName === undefined) {
      toastRef.current?.toast("Please select index", "error", 3000, { vertical: "top", horizontal: "center" });
      return;
    }

    setIsLoading(true);
    const embeddingResult = await getEmbeddingText(question);

    const param: any = {
      command: "get_doc",
      index_name: selectedIndexName,
      options: {
        answer_num: 10,
        vector: embeddingResult,
        vector_column_name: "embedding",
        vector_num: 10,
      },
    };

    const res = await vectoCoreApi.auth_request("api", param, props.tennantInfo.tennant_key);
    if (res.code === "200") {
      console.log("answer : ", res);
      setAnswers(res.response.doc_list);
      popAnswerRef.current.open(true);
    } else {
      toastRef.current?.toast("Error fncQuestion : " + res.response.error_msg, "error", 3000, { vertical: "top", horizontal: "center" });
    }
    setIsLoading(false);
  };

  const handleAttachInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const acceptableFormats = fileGroup;
    if (e.target.files !== null) {
      const files: FileList = e.target.files;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!acceptableFormats.includes(file.type)) {
          alert("지원하지 않는 포멧입니다.");
          return;
        }
      }

      const splitter = new CharacterTextSplitter({
        chunkSize: 1,
        chunkOverlap: 0,
      });

      setIsLoading(true);
      for await (const fileInfo of files) {
        const text = await fileInfo.text();
        const spritTexts: any = await splitter.createDocuments([text]);
        console.log("spritTexts : ", spritTexts);

        const fileName: string = fileInfo.name;
        let index: number = 0;

        for await (const spritText of spritTexts) {
          const id: string = `${fileName}-${String(index).padStart(5, "0")}`;
          const meta: string = `{ 'source':${fileName}, 'page':'${index}'}`;
          console.log("spritText : ", spritText.pageContent);
          const embeddingResult = await getEmbeddingText(spritText.pageContent);
          console.log("embeddingResult : ", embeddingResult);

          if (embeddingResult === null) {
            toastRef.current?.toast("Error embedding", "error", 3000, { vertical: "top", horizontal: "center" });
            attachInputRef.current.value = "";
            setIsLoading(false);
            getDocs(selectedIndexName);
            return;
          }
          const param: any = {
            command: "create_doc",
            index_name: selectedIndexName,
            doc_body: {
              id: id,
              text: spritText.pageContent,
              metadata: meta,
              embedding: embeddingResult,
            },
          };
          const finalResult = await vectoCoreApi.auth_request("api", param, props.tennantInfo.tennant_key);
          console.log("finalResult [" + index + "] : ", finalResult);
          index = index + 1;
          const progress = (index / spritTexts.length) * 100;
          setProgress(progress);
        }
      }
      attachInputRef.current.value = "";
      setIsLoading(false);
      getDocs(selectedIndexName);
    }
  };

  return (
    <>
      <Box sx={{ p: 0 }}>
        <Stack direction={"column"} spacing={0} sx={{ height: "100vh", overflow: "auto" }}>
          <Box className="my-account-header-root">
            <Typography variant="h5" gutterBottom className="my-account-header-title">
              Application Documents
            </Typography>
          </Box>
          <Divider />
          <Box className="my-account-content-root">
            <Box className="apikey-content-header">
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography gutterBottom className="my-account-content-sub-title">
                  Manage Documents in Index here
                </Typography>
              </Box>
            </Box>
            <Stack direction="column" spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="lableCollection" size="small">
                  Collection
                </InputLabel>
                <Select
                  labelId="lableCollection"
                  id="selectedCollection"
                  value={selectedIndexName}
                  label="Index"
                  onChange={handelSelectedIndexChange}
                  size="small"
                >
                  <MenuItem key={"-1"} value="-1">
                    선택
                  </MenuItem>
                  {indexes.map((name: any, index: number) => (
                    <MenuItem key={index + "" + name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="lableModel" size="small">
                  Model
                </InputLabel>
                <Select
                  labelId="lableModel"
                  id="selectedModel"
                  value={selectedModel}
                  label="Model"
                  onChange={handelSelectedModelChange}
                  size="small"
                >
                  <MenuItem value={"naver"}>Naver</MenuItem>
                  <MenuItem value={"openai"}>OpenAI</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  attachInputRef.current?.click();
                }}
              >
                Add Document
              </Button>
              {progress > 0 && <LinearProgress variant="determinate" value={progress} />}
              <TableContainer component={Paper}>
                <Table className="data-table">
                  <TableBody>
                    {docs.map((docInfo: any, index: number) => (
                      <DocsRow key={docInfo.id + "" + index} docInfo={docInfo} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Box>
          {selectedIndexName !== "-1" && (
            <>
              <Box sx={{ p: "12px" }}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    id="txtQuestion"
                    value={question}
                    placeholder="Test Question"
                    variant="outlined"
                    fullWidth
                    required
                    size="small"
                    onChange={(e) => {
                      setQuestion(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") fncQuestion();
                    }}
                  />
                  <Button
                    variant="outlined"
                    sx={{ minWidth: "200px" }}
                    onClick={() => {
                      fncQuestion();
                    }}
                  >
                    Question
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </Box>
      <LoadingCircle loading={isLoading} />
      <Toast ref={toastRef} />
      <PopAnswers ref={popAnswerRef} question={question} answers={answers} />
      <Input
        key="attachment"
        color="primary"
        type="file"
        inputProps={{ multiple: true }}
        inputRef={attachInputRef}
        onChange={handleAttachInput}
        sx={{ display: "none" }}
      />
    </>
  );
};

export default MngDocs;
